import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './LaborRulesDashboard.css';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';

interface LaborRule {
  id: string;
  name: string;
  description: string;
  ruleType: 'overtime' | 'break_time' | 'meal_period' | 'minimum_wage' | 'maximum_hours' | 'rest_period' | 'split_shift' | 'call_in_pay' | 'reporting_pay';
  scopeType: 'enterprise' | 'concept' | 'franchisee' | 'organizational_unit' | 'store' | 'state' | 'federal';
  scopeId?: string;
  jurisdiction?: string;
  ruleConfiguration: any;
  conditions: any;
  priority: number;
  effectiveDate: string;
  expirationDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RuleTemplate {
  name: string;
  description: string;
  ruleConfiguration: any;
}

interface LaborRulesDashboardProps {
  enterpriseId: string;
  storeId?: string;
}

export const LaborRulesDashboard: React.FC<LaborRulesDashboardProps> = ({ 
  enterpriseId, 
  storeId 
}) => {
  const [rules, setRules] = useState<LaborRule[]>([]);
  const [templates, setTemplates] = useState<Record<string, RuleTemplate[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRule, setSelectedRule] = useState<LaborRule | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [filterRuleType, setFilterRuleType] = useState<string>('all');
  const [filterScope, setFilterScope] = useState<string>('all');
  const [testResults, setTestResults] = useState<any>(null);

  const [newRule, setNewRule] = useState<{
    name: string;
    description: string;
    ruleType: string;
    scopeType: string;
    scopeId: string;
    jurisdiction: string;
    ruleConfiguration: Record<string, any>;
    conditions: Record<string, any>;
    priority: number;
    effectiveDate: string;
    expirationDate: string;
    isActive: boolean;
  }>({
    name: '',
    description: '',
    ruleType: 'overtime',
    scopeType: 'enterprise',
    scopeId: enterpriseId,
    jurisdiction: '',
    ruleConfiguration: {},
    conditions: {},
    priority: 100,
    effectiveDate: new Date().toISOString().split('T')[0],
    expirationDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchRules();
    fetchTemplates();
  }, [filterRuleType, filterScope, enterpriseId, storeId]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '100'
      });

      if (filterRuleType !== 'all') params.append('ruleType', filterRuleType);
      if (filterScope !== 'all') params.append('scopeType', filterScope);
      if (storeId) params.append('scopeId', storeId);

      const response = await authenticatedFetch(`${API_ENDPOINTS.laborRules()}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch labor rules');

      const data = await response.json();
      setRules(data.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const ruleTypes = ['overtime', 'break_time', 'meal_period', 'rest_period'];
      const templateData: Record<string, RuleTemplate[]> = {};

      for (const ruleType of ruleTypes) {
        const response = await authenticatedFetch(API_ENDPOINTS.laborRulesTemplates(ruleType));
        if (response.ok) {
          const data = await response.json();
          templateData[ruleType] = data.data ? [data.data] : [];
        }
      }

      setTemplates(templateData);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleCreateRule = async () => {
    try {
      if (!newRule.name) {
        alert('Please enter a rule name');
        return;
      }

      if (newRule.scopeType === 'state' && !newRule.jurisdiction) {
        alert('Please select a state for state-level rules');
        return;
      }

      const response = await authenticatedFetch(API_ENDPOINTS.laborRules(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRule),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create labor rule');
      }

      setNewRule({
        name: '',
        description: '',
        ruleType: 'overtime',
        scopeType: 'enterprise',
        scopeId: enterpriseId,
        jurisdiction: '',
        ruleConfiguration: {},
        conditions: {},
        priority: 100,
        effectiveDate: new Date().toISOString().split('T')[0],
        expirationDate: '',
        isActive: true
      });
      setShowAddForm(false);
      fetchRules();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create rule');
    }
  };

  const handleUpdateRule = async (ruleId: string, updates: Partial<LaborRule>) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.laborRule(ruleId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) throw new Error('Failed to update labor rule');

      fetchRules();
      setSelectedRule(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this rule?')) return;

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.laborRule(ruleId), {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to deactivate labor rule');

      fetchRules();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate rule');
    }
  };

  const handleTestRule = async () => {
    try {
      const testTimeEntry = {
        employeeId: 'test-employee',
        storeId: storeId || 'test-store',
        totalMinutes: 480, // 8 hours
        clockInTime: new Date().toISOString(),
        clockOutTime: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString()
      };

      const response = await authenticatedFetch(API_ENDPOINTS.laborRulesTestOvertime(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ timeEntry: testTimeEntry }),
      });

      if (!response.ok) throw new Error('Failed to test rule');

      const result = await response.json();
      setTestResults(result);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to test rule');
    }
  };

  const applyTemplate = (template: RuleTemplate) => {
    setNewRule({
      ...newRule,
      name: template.name,
      description: template.description,
      ruleConfiguration: template.ruleConfiguration
    });
    setShowTemplates(false);
  };

  const getRuleTypeColor = (ruleType: string) => {
    const colors = {
      overtime: '#007bff',
      break_time: '#28a745',
      meal_period: '#ffc107',
      minimum_wage: '#dc3545',
      maximum_hours: '#6f42c1',
      rest_period: '#17a2b8',
      split_shift: '#fd7e14',
      call_in_pay: '#20c997',
      reporting_pay: '#6c757d'
    };
    return colors[ruleType as keyof typeof colors] || '#6c757d';
  };

  const getScopeIcon = (scopeType: string) => {
    const icons = {
      federal: 'FED',
      state: 'STATE',
      enterprise: 'ENT',
      concept: 'CONCEPT',
      franchisee: 'FRAN',
      store: 'STORE'
    };
    return icons[scopeType as keyof typeof icons] || 'OTHER';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const ruleTypes = [
    { value: 'overtime', label: 'Overtime' },
    { value: 'break_time', label: 'Break Time' },
    { value: 'meal_period', label: 'Meal Period' },
    { value: 'minimum_wage', label: 'Minimum Wage' },
    { value: 'maximum_hours', label: 'Maximum Hours' },
    { value: 'rest_period', label: 'Rest Period' },
    { value: 'split_shift', label: 'Split Shift' },
    { value: 'call_in_pay', label: 'Call-in Pay' },
    { value: 'reporting_pay', label: 'Reporting Pay' }
  ];

  const scopeTypes = [
    { value: 'federal', label: 'Federal' },
    { value: 'state', label: 'State' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'concept', label: 'Concept' },
    { value: 'franchisee', label: 'Franchisee' },
    { value: 'store', label: 'Store' }
  ];

  return (
    <div className="labor-rules-dashboard">
      <div className="dashboard-header">
        <h2>Labor Rules Configuration</h2>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => {
              console.log('[LaborRules] Templates button clicked');
              setShowTemplates(true);
            }}
          >
            Templates
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              console.log('[LaborRules] Test Rules button clicked');
              handleTestRule();
            }}
          >
            Test Rules
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              console.log('[LaborRules] Add Rule button clicked');
              setShowAddForm(true);
            }}
          >
            Add Rule
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading labor rules...</div>}
      {error && <div className="error" style={{ margin: '10px 0', padding: '10px', background: '#fee', borderRadius: '4px' }}>Error: {error}</div>}

      <div className="filters">
        <div className="filter-group">
          <label>Rule Type:</label>
          <select
            value={filterRuleType}
            onChange={(e) => setFilterRuleType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            {ruleTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Scope:</label>
          <select
            value={filterScope}
            onChange={(e) => setFilterScope(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Scopes</option>
            {scopeTypes.map((scope) => (
              <option key={scope.value} value={scope.value}>
                {scope.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rules-grid">
        {rules.map((rule) => (
          <div 
            key={rule.id} 
            className={`rule-card ${!rule.isActive ? 'inactive' : ''}`}
            onClick={() => setSelectedRule(rule)}
          >
            <div className="rule-header">
              <div className="rule-type-badge" style={{ backgroundColor: getRuleTypeColor(rule.ruleType) }}>
                {rule.ruleType?.replace('_', ' ') || 'Unknown'}
              </div>
              <div className="rule-scope">
                {getScopeIcon(rule.scopeType)} {rule.scopeType}
              </div>
            </div>
            
            <div className="rule-content">
              <h4 className="rule-name">{rule.name}</h4>
              <p className="rule-description">{rule.description}</p>
              
              <div className="rule-details">
                <div className="detail-item">
                  <span className="label">Priority:</span>
                  <span className="value">{rule.priority}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Effective:</span>
                  <span className="value">{formatDate(rule.effectiveDate)}</span>
                </div>
                {rule.jurisdiction && (
                  <div className="detail-item">
                    <span className="label">Jurisdiction:</span>
                    <span className="value">{rule.jurisdiction}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rule-actions">
              <button
                className="btn btn-small btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateRule(rule.id, { isActive: !rule.isActive });
                }}
              >
                {rule.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                className="btn btn-small btn-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRule(rule.id);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {rules.length === 0 && (
        <div className="empty-state">
          <h3>No Labor Rules Found</h3>
          <p>Create your first labor rule to get started with compliance management.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            Add Your First Rule
          </button>
        </div>
      )}

      {showTemplates && ReactDOM.createPortal(
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Rule Templates</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setShowTemplates(false)}
              >
                Close
              </button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div className="templates-grid">
                {Object.entries(templates).map(([ruleType, typeTemplates]) => (
                  <div key={ruleType} className="template-section">
                    <h4>{ruleType?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}</h4>
                    {typeTemplates.map((template, index) => (
                      <div key={index} className="template-card">
                        <h5>{template.name}</h5>
                        <p>{template.description}</p>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => applyTemplate(template)}
                        >
                          Use Template
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showAddForm && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Add New Labor Rule</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Rule Name:</label>
                  <input
                    type="text"
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    className="form-input"
                    placeholder="e.g., California Daily Overtime"
                  />
                </div>

                <div className="form-group">
                  <label>Rule Type:</label>
                  <select
                    value={newRule.ruleType}
                    onChange={(e) => setNewRule({...newRule, ruleType: e.target.value})}
                    className="form-input"
                  >
                    {ruleTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Scope Type:</label>
                  <select
                    value={newRule.scopeType}
                    onChange={(e) => setNewRule({...newRule, scopeType: e.target.value, jurisdiction: ''})}
                    className="form-input"
                  >
                    {scopeTypes.map((scope) => (
                      <option key={scope.value} value={scope.value}>
                        {scope.label}
                      </option>
                    ))}
                  </select>
                </div>

                {newRule.scopeType === 'state' && (
                  <div className="form-group">
                    <label>State(s): *</label>
                    <select
                      value={newRule.jurisdiction}
                      onChange={(e) => setNewRule({...newRule, jurisdiction: e.target.value})}
                      className="form-input"
                      required
                    >
                      <option value="">Select a state...</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="DC">District of Columbia</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Priority (0-1000):</label>
                  <input
                    type="number"
                    value={newRule.priority}
                    onChange={(e) => setNewRule({...newRule, priority: parseInt(e.target.value) || 100})}
                    className="form-input"
                    min="0"
                    max="1000"
                  />
                </div>

                <div className="form-group">
                  <label>Effective Date:</label>
                  <input
                    type="date"
                    value={newRule.effectiveDate}
                    onChange={(e) => setNewRule({...newRule, effectiveDate: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description:</label>
                  <textarea
                    value={newRule.description}
                    onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                    className="form-input"
                    rows={3}
                    placeholder="Describe when and how this rule applies..."
                  />
                </div>

              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleCreateRule}>
                  Create Rule
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {selectedRule && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Rule Details</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedRule(null)}
              >
                Close
              </button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div className="rule-details-view">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Name:</label>
                      <span>{selectedRule.name}</span>
                    </div>
                    <div className="detail-item">
                      <label>Type:</label>
                      <span>{selectedRule.ruleType?.replace('_', ' ') || 'Unknown'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Scope:</label>
                      <span>{selectedRule.scopeType}</span>
                    </div>
                    <div className="detail-item">
                      <label>Priority:</label>
                      <span>{selectedRule.priority}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={selectedRule.isActive ? 'status-active' : 'status-inactive'}>
                        {selectedRule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Effective Date:</label>
                      <span>{formatDate(selectedRule.effectiveDate)}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Configuration</h4>
                  <pre className="config-display">
                    {JSON.stringify(selectedRule.ruleConfiguration, null, 2)}
                  </pre>
                </div>

                <div className="detail-section">
                  <h4>Description</h4>
                  <p>{selectedRule.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {testResults && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Rule Test Results</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setTestResults(null)}
              >
                Close
              </button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div className="test-results">
                <h4>Input:</h4>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
                  {JSON.stringify(testResults.input, null, 2)}
                </pre>

                <h4>Calculation Result:</h4>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
                  {JSON.stringify(testResults.calculation, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};